import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Check, Gift } from 'lucide-react'
import Header from '../components/Header'
import { useI18n } from '../i18n/useI18n'
import { useStore } from '../store/useStore'
import { api, ApiError } from '../services/api'

type Result = {
  sponsor_name: string
  amount_jpy: number
  expires_at: string
}

/**
 * #1433 スポンサーポイントをコードで受け取る。
 *
 * 自治体の既存業務（対象者の選定・通知書の郵送）をそのまま使える形。紙の商品券の
 * 「引換券を郵送 → 窓口で引き換え」の引き換え先がアプリになるだけ。
 *
 * **期限はコードの発行日から起算していて、入力しても延びない。** 客が誤解しないよう、
 * 受け取り後に必ず失効日を出す。
 */
export default function RedeemPointCode() {
  const navigate = useNavigate()
  const { t } = useI18n()
  const setBalance = useStore((s) => s.setBalance)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<Result | null>(null)

  const submit = async () => {
    const value = code.trim().toUpperCase()
    if (!value || busy) return
    setBusy(true)
    setError(null)
    try {
      const res = await api.redeemSponsorCode(value)
      setResult(res)
      setBalance(res.new_balance)
    } catch (e) {
      // **理由を区別して出す。** 「無効」で一括りにすると、期限切れなのか
      // 打ち間違いなのか客が判断できず、同じコードを何度も試すことになる
      // (総当たり対策のレート制限にも引っかかる)。
      //
      // ApiError が backend の `detail.code` をそのまま持っている (#392)。
      const reason = e instanceof ApiError ? e.code : undefined
      setError(
        reason === 'already_redeemed' ? t('pointCode.errorUsed')
          : reason === 'expired' ? t('pointCode.errorExpired')
            : reason === 'too_many_attempts' ? t('pointCode.errorTooMany')
              : t('pointCode.errorInvalid'),
      )
    } finally {
      setBusy(false)
    }
  }

  if (result) {
    return (
      <div className="min-h-screen bg-warm-white safe-top safe-bottom">
        <Header title={t('pointCode.title')} onBack={() => navigate('/wallet')} />
        <div className="animate-fade-in px-4 pt-10 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-matcha/15">
            <Check size={30} className="text-matcha" />
          </div>
          <p className="mt-4 font-body text-sm text-text-secondary">{result.sponsor_name}</p>
          <p className="mt-1 font-display text-3xl font-bold text-navy">
            &yen; {result.amount_jpy.toLocaleString()}
          </p>
          <p className="mt-3 font-body text-xs text-text-secondary">
            {t('pointCode.expiresOn', { date: result.expires_at })}
          </p>
          {/* 期限はコード発行日から起算していて、受け取り日からではない */}
          <p className="mt-1 font-body text-[11px] text-text-tertiary">
            {t('pointCode.expiryNote')}
          </p>
          <button
            onClick={() => navigate('/wallet')}
            className="tap-effect mt-8 h-12 w-full rounded-xl bg-navy font-display text-sm font-semibold text-white"
          >
            {t('pointCode.toWallet')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-warm-white safe-top safe-bottom">
      <Header title={t('pointCode.title')} onBack={() => navigate(-1)} />
      <div className="px-4 pt-6">
        <div className="flex items-center gap-3 rounded-xl bg-white p-4 shadow-card">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gold/15">
            <Gift size={20} className="text-gold" />
          </div>
          <p className="font-body text-xs leading-5 text-text-secondary">
            {t('pointCode.intro')}
          </p>
        </div>

        <input
          value={code}
          onChange={(e) => { setCode(e.target.value); setError(null) }}
          onKeyDown={(e) => { if (e.key === 'Enter') submit() }}
          autoCapitalize="characters"
          autoComplete="off"
          spellCheck={false}
          placeholder={t('pointCode.placeholder')}
          className="mt-5 h-14 w-full rounded-xl border border-gray-200 bg-white px-4 text-center font-mono text-lg tracking-[0.2em] text-text-primary outline-none focus:border-navy"
        />

        {error && (
          <p className="mt-2 font-body text-xs text-yokoso-red">{error}</p>
        )}

        <button
          onClick={submit}
          disabled={busy || !code.trim()}
          className={`tap-effect mt-5 h-12 w-full rounded-xl font-display text-sm font-semibold text-white ${
            busy || !code.trim() ? 'bg-gray-300' : 'bg-yokoso-red'
          }`}
        >
          {busy ? t('pointCode.checking') : t('pointCode.submit')}
        </button>

        <p className="mt-4 font-body text-[11px] leading-5 text-text-tertiary">
          {t('pointCode.note')}
        </p>
      </div>
    </div>
  )
}
